import { endpointsList } from '@/helpers';
import axios from 'axios';
import { isNil } from 'lodash';
import {
  CheckConfiguration,
  CheckProperty,
  ICheckBotConfigurationDto,
  IConfigsListDto,
  IConfigTemplateDto,
  ICreateCheckBotConfigurationDto,
  ITemplatesListDto,
  KnownCheckBotValueType,
} from 'types';

export type CheckPropertyValidationResult = {
  isValid: boolean;
  errorType?: CheckPropertyValidationErrorType;
};
export enum CheckPropertyValidationErrorType {
  MISSING_REQUIRED = 'missingRequired',
  TOO_HIGH = 'tooHigh',
  TOO_HIGH_EXCLUSIVE = 'tooHighExclusive',
  TOO_LOW = 'tooLow',
  TOO_LOW_EXCLUSIVE = 'tooLowExclusive',
  TOO_MANY_ITEMS = 'tooManyItems',
  TOO_FEW_ITEMS = 'tooFewItems',
  IS_NOT_INTEGER = 'isNotInteger',
}

export class CheckBotConfigsService {
  public static async getCheckBotConfigs(
    projectId: string,
    supplierId?: string,
  ): Promise<IConfigsListDto> {
    const response = await axios.get<IConfigsListDto>(
      endpointsList.checkBot.configs.getAssignedConfigs(projectId, supplierId),
    );
    return response.data;
  }

  public static async getConfigTemplates(): Promise<ITemplatesListDto> {
    const response = await axios.get<ITemplatesListDto>(
      endpointsList.checkBot.configs.getTemplates(),
    );
    return response.data;
  }

  public static async getConfigOrTemplate(
    configId?: string,
    templateId?: string,
  ): Promise<ICheckBotConfigurationDto | IConfigTemplateDto> {
    if (configId) {
      const response = await axios.get<ICheckBotConfigurationDto>(
        endpointsList.checkBot.configs.getConfig(configId),
      );
      return response.data;
    } else if (templateId) {
      const response = await axios.get<IConfigTemplateDto>(
        endpointsList.checkBot.configs.getTemplate(parseInt(templateId)),
      );
      return response.data;
    } else throw new Error('Neither configId nor templateId provided');
  }

  public static async getCheckSchemas(): Promise<CheckConfiguration[]> {
    const response = await axios.get<CheckConfiguration[]>(
      endpointsList.checkBot.configs.getChecks(),
    );
    return response.data;
  }

  public static async createConfig(
    config: ICreateCheckBotConfigurationDto,
    projectId: string,
    supplierId?: string,
  ): Promise<void> {
    this.filterOutEmptyParams(config);
    await axios.post(
      endpointsList.checkBot.configs.createConfig(projectId, supplierId),
      config,
    );
  }

  public static async updateConfig(
    configId: string,
    config: ICreateCheckBotConfigurationDto,
  ): Promise<void> {
    this.filterOutEmptyParams(config);
    await axios.put(
      endpointsList.checkBot.configs.updateConfig(configId),
      config,
    );
  }

  private static filterOutEmptyParams(
    config: ICreateCheckBotConfigurationDto,
  ): void {
    config.checks.forEach((check) => {
      check.params = check.params.filter(
        (param) => !isNil(param.value) && param.value !== '',
      );
    });
  }

  public static validateCheckBotConfiguration(
    config: ICreateCheckBotConfigurationDto,
    checkSchemas: CheckConfiguration[],
  ): boolean {
    return (
      !!config.name &&
      !!config.checks.length &&
      config.checks.every((check) => {
        const checkSchema = checkSchemas.find(
          (checkSchema) => checkSchema.name === check.name,
        );
        const arePropertiesValid = checkSchema?.checkProperties.every(
          (checkProperty) => {
            const checkParam = check.params.find(
              (param) => param.name === checkProperty.name,
            );
            return this.validateCheckProperty(
              checkProperty,
              checkParam?.value ?? null,
            ).isValid;
          },
        );
        const areResultsValid =
          !!check.results.length &&
          check.results.every((result) => result.status);
        return arePropertiesValid && areResultsValid;
      })
    );
  }

  public static validateCheckProperty(
    propertySchema: CheckProperty,
    value: KnownCheckBotValueType | null,
  ): CheckPropertyValidationResult {
    const errorType = CheckBotConfigsService.getErrorType(
      propertySchema,
      value,
    );
    return {
      isValid: !errorType,
      errorType,
    };
  }

  private static getErrorType(
    propertySchema: CheckProperty,
    value: KnownCheckBotValueType | null,
  ): CheckPropertyValidationErrorType | undefined {
    const isMissingRequired =
      propertySchema.isRequired && (isNil(value) || value === '');
    const isNumeric = this.isNumericTypeSchema(propertySchema);
    const isTooHigh =
      isNumeric &&
      propertySchema.max !== null &&
      typeof value === 'number' &&
      (value > propertySchema.max ||
        (value === propertySchema.max && propertySchema.isExclusiveMaximum));
    const isTooLow =
      isNumeric &&
      propertySchema.min !== null &&
      typeof value === 'number' &&
      (value < propertySchema.min ||
        (value === propertySchema.min && propertySchema.isExclusiveMinimum));
    const isTooHighExclusive = isTooHigh && propertySchema.isExclusiveMaximum;
    const isTooLowExclusive = isTooLow && propertySchema.isExclusiveMinimum;
    const isNotInteger =
      isNumeric &&
      propertySchema.type === 'integer' &&
      !isNil(value) &&
      value !== '' &&
      !Number.isInteger(value);

    const isTooManyItems =
      propertySchema.type === 'array' &&
      Array.isArray(value) &&
      propertySchema.maxItems !== null &&
      value.length > propertySchema.maxItems;
    const isTooFewItems =
      propertySchema.type === 'array' &&
      Array.isArray(value) &&
      propertySchema.minItems !== null &&
      value.length < propertySchema.minItems;

    switch (true) {
      case isMissingRequired:
        return CheckPropertyValidationErrorType.MISSING_REQUIRED;
      case isTooHighExclusive:
        return CheckPropertyValidationErrorType.TOO_HIGH_EXCLUSIVE;
      case isTooHigh:
        return CheckPropertyValidationErrorType.TOO_HIGH;
      case isTooLowExclusive:
        return CheckPropertyValidationErrorType.TOO_LOW_EXCLUSIVE;
      case isTooLow:
        return CheckPropertyValidationErrorType.TOO_LOW;
      case isNotInteger:
        return CheckPropertyValidationErrorType.IS_NOT_INTEGER;
      case isTooManyItems:
        return CheckPropertyValidationErrorType.TOO_MANY_ITEMS;
      case isTooFewItems:
        return CheckPropertyValidationErrorType.TOO_FEW_ITEMS;
    }
  }

  public static isNumericTypeSchema(
    propertySchema: CheckProperty,
  ): propertySchema is CheckProperty & { type: 'number' | 'integer' } {
    return (
      propertySchema.type === 'number' || propertySchema.type === 'integer'
    );
  }

  public static isArrayTypeSchema(
    propertySchema: CheckProperty,
  ): propertySchema is CheckProperty & { type: 'array' } {
    return propertySchema.type === 'array';
  }
}
